/**
 * Smart chip generation utility
 *
 * Generates clean, round chip values based on player's balance and bet limits.
 * Works across different currencies by scaling with maxBet.
 */

// Default chip multipliers from backend - used to detect custom operator config
export const DEFAULT_CHIP_MULTIPLIERS = [0.1, 0.5, 1, 5, 10, 25, 50, 100, 250, 500, 1000]

// Nice round values for smart chip generation
// Includes high values for currencies like KES where 1000 KES ≈ $8 USD
// Balance filtering ensures players only see chips they can afford
export const NICE_CHIP_VALUES = [
  0.0001, 0.0002, 0.0005, 0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50,
  100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000
]

/**
 * Check if operator has custom chip multipliers configured
 * Custom = different from the default backend values
 */
export function hasCustomChipMultipliers(multipliers: number[]): boolean {
  if (multipliers.length !== DEFAULT_CHIP_MULTIPLIERS.length) return true
  return !multipliers.every((m, i) => m === DEFAULT_CHIP_MULTIPLIERS[i])
}

export interface GenerateSmartChipsOptions {
  minBet: number
  maxBet: number
  balance: number
  targetCount?: number
  maxBetPercentage?: number
}

/**
 * Generate smart chip values based on balance and bet limits
 * Returns clean, round numbers appropriate for the user's balance
 *
 * @param options.minBet - Minimum bet amount
 * @param options.maxBet - Maximum bet amount
 * @param options.balance - Player's current balance (use initial snapshot)
 * @param options.targetCount - Number of chips to generate (default: 5)
 * @param options.maxBetPercentage - Max chip as percentage of maxBet (default: 0.4 = 40%)
 */
export function generateSmartChips({
  minBet,
  maxBet,
  balance,
  targetCount = 5,
  maxBetPercentage = 0.4
}: GenerateSmartChipsOptions): number[] {
  // Cap max chip at percentage of maxBet (scales naturally with currency)
  // This prevents showing $2000 chips for USD while allowing 50000 KES chips
  const maxChipValue = maxBet * maxBetPercentage

  // Filter to values that are >= minBet and <= maxChipValue
  const validChips = NICE_CHIP_VALUES.filter((v) => v >= minBet && v <= maxChipValue)

  if (validChips.length === 0) {
    // Fallback: just use minBet if no nice values fit
    return [minBet]
  }

  // $0 balance: show smallest tier (first targetCount valid chips)
  if (balance <= 0) {
    return validChips.slice(0, targetCount)
  }

  // Filter to values <= affordable amount
  const maxAllowed = Math.min(maxBet, balance)
  const affordableChips = validChips.filter((v) => v <= maxAllowed)

  if (affordableChips.length === 0) {
    // Can't afford any nice value, just use minBet
    return [minBet]
  }

  if (affordableChips.length <= targetCount) {
    return affordableChips
  }

  // Pick well-distributed values across the range
  const result: number[] = []
  const step = (affordableChips.length - 1) / (targetCount - 1)

  for (let i = 0; i < targetCount; i++) {
    const index = Math.round(i * step)
    const value = affordableChips[index]
    if (value !== undefined) {
      result.push(value)
    }
  }

  // For users with balance < $100 (or equivalent in other currencies),
  // ensure smallest chip is included so they have affordable betting options.
  // We use 4% of maxBet as this scales naturally: 4% of $2500 = $100 USD,
  // 4% of 325,000 KES = 13,000 KES (~$100 USD equivalent)
  const lowBalanceThreshold = maxBet * 0.04
  if (balance < lowBalanceThreshold) {
    const smallestAffordableChip = affordableChips[0]
    if (smallestAffordableChip !== undefined && !result.includes(smallestAffordableChip)) {
      result.unshift(smallestAffordableChip)
      // Keep only targetCount chips
      if (result.length > targetCount) {
        result.pop()
      }
    }
  }

  return result
}

/**
 * Generate chip amounts from multipliers (original behavior for custom operator configs)
 *
 * Chips are `minBet * multiplier`, so multipliers below 1 (the default set
 * includes 0.1 and 0.5) would produce chips *below* the minimum bet — which
 * can't be validly placed and make the chip set look mismatched against the
 * min bet (QA-60081). Drop any sub-minimum chips and always guarantee at
 * least one chip that covers the min bet.
 */
export function generateChipsFromMultipliers(multipliers: number[], minBet: number): number[] {
  const chips = multipliers
    .map((multiplier) => minBet * multiplier)
    .filter((value) => value >= minBet)
  return chips.length > 0 ? chips : [minBet]
}

// Below this a min bet formats with leading-zero decimals (e.g. 0.000015 for
// BTC, 0.0005 for ETH) that don't fit the fixed-size chip circle. For those
// (crypto) currencies the smallest clean chip already covers the minimum, so
// we skip the extra chip rather than render a cramped label.
export const MIN_BET_CHIP_MIN_VALUE = 0.1

/**
 * Prepend the exact min bet as an extra leading chip when the smallest
 * generated chip is larger than the minimum (the min bet sits between nice
 * values, e.g. a 0.75 min bet whose smallest clean chip is 1). This lets
 * players place the exact minimum without dropping the clean values. As the
 * leftmost chip it shows first on desktop, while the mobile chip strip starts
 * its window past it so it stays off-screen until the player swipes back to
 * it (QA-60081). No-op when the min is already the smallest chip, the list is
 * empty, or the min is too small to label legibly (see MIN_BET_CHIP_MIN_VALUE).
 */
export function prependMinBetChip(values: number[], minBet: number): number[] {
  const smallest = values[0]
  if (smallest === undefined || smallest <= minBet || minBet < MIN_BET_CHIP_MIN_VALUE) {
    return values
  }
  return [minBet, ...values]
}
